/**
 * @file    useEngineValidation.ts
 * @scope   ENTERPRISE
 * @summary Save-guard predicates: active subtype validator + Enterprise FieldsManager guard.
 * @consumes EnterpriseContext, fieldMapping + fieldValidation (DiyEnterprise domain)
 * @produces `isFieldsManagerSaveDisabled`, `isEnterpriseWidgetInvalid`
 * @sideEffects None — pure reads over the Enterprise context.
 * @notes Exposed as callbacks so the shared `isSaveDisabled` guard stays domain-agnostic (DIP).
 */
import { getSelectedUserFields } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/mapping/fieldMapping';
import { isFieldItemInvalid } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/validation/fieldValidation';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { isNewerThanKnown } from 'components/DiyWidgetEngine/engine/serialization/envelope';
import { ActiveEngineDescriptor } from './useActiveEngineDescriptor';
import { useActiveEngineSlice } from './useActiveEngineSlice';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

export interface EngineValidation {
  /** True when the FieldsManager "Save" button must stay disabled. */
  isFieldsManagerSaveDisabled: (selectedPartition: any) => boolean;
  /** True when the Enterprise part of the widget config is not savable yet. */
  isEnterpriseWidgetInvalid: (customWidgetConfig: any) => boolean;
}

export function useEngineValidation(descriptor?: ActiveEngineDescriptor): EngineValidation {
  const { state, isInvalid } = useEnterpriseContext();
  // WHY: the subtype validator must read ITS slice (CPR ≠ Enterprise state).
  const { slice } = useActiveEngineSlice(descriptor);

  const isFieldsManagerSaveDisabled = (selectedPartition: any): boolean => {
    const userFields = getSelectedUserFields(
      selectedPartition,
      state.selectedValues.selectedEnterpriseFields
    );
    // WHY: a partition with no fields at all can never produce a valid widget.
    if (!userFields || !userFields.fields || userFields.fields.length === 0) return true;
    const invalidFields = userFields.fields.filter((field, i) =>
      isFieldItemInvalid(field, i, new Set(userFields.fields.map((f, i) => i)))
    );
    return invalidFields.length > 0;
  };

  // WHY: delegate to the active subtype's validator (§6); fall back to the context selector when
  // no descriptor is active or it declares no validation.
  const isEnterpriseWidgetInvalid = (customWidgetConfig: any): boolean => {
    // WHY: a payload written by a newer client is read-only — never re-save and downgrade it (§7.1).
    // TODO(ai): this only disables Save; add a visible "read-only" notice in the drawer.
    const knownVersion = descriptor?.serializerDelta?.version;
    if (knownVersion && isNewerThanKnown(customWidgetConfig, knownVersion)) return true;
    return descriptor?.validation
      ? descriptor.validation.isInvalid(slice, descriptor.capabilities)
      : isInvalid(customWidgetConfig);
  };

  return { isFieldsManagerSaveDisabled, isEnterpriseWidgetInvalid };
}

export default useEngineValidation;
